'use client';

import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { BaseModal } from '@components/base-modal';
import { Button } from '@components/button';
import { ButtonIcon } from '@components/button-icon';
import { Typography } from '@components/typography';
import { Icon, IconNames } from '@components/icon';
import { Link } from '@components/link';
import { routes } from '@routes';

import {
  CookieCategory,
  CookieConsentPreferences,
  COOKIE_CATEGORIES,
  DEFAULT_PREFERENCES,
} from './utils';

interface CookiePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    preferences: Omit<
      CookieConsentPreferences,
      'consentDate' | 'consentVersion'
    >
  ) => void;
  initialPreferences?: CookieConsentPreferences | null;
}

interface CategoryToggleProps {
  category: (typeof COOKIE_CATEGORIES)[number];
  isEnabled: boolean;
  onToggle: (categoryId: CookieCategory, enabled: boolean) => void;
  isExpanded: boolean;
  onExpandToggle: () => void;
}

const CategoryToggle: React.FC<CategoryToggleProps> = ({
  category,
  isEnabled,
  onToggle,
  isExpanded,
  onExpandToggle,
}) => {
  return (
    <div className="border-b border-gray-light last:border-b-0">
      <div className="flex items-center justify-between py-4 px-1">
        <button
          type="button"
          onClick={onExpandToggle}
          className="flex items-center gap-3 flex-1 text-left group"
          aria-expanded={isExpanded}
        >
          <span
            className="transition-transform duration-200"
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <Icon
              name={IconNames.CHEVRON_DOWN}
              size="sm"
              className="text-gray-main"
            />
          </span>
          <Typography
            variant="body1"
            component="span"
            className="text-primary-main font-medium group-hover:text-primary-light transition-colors"
          >
            {category.name}
          </Typography>
          {category.isRequired && (
            <span className="text-xs bg-pool text-primary-main px-2 py-0.5 rounded-full font-medium">
              Required
            </span>
          )}
        </button>

        {/* Toggle Switch */}
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={(e) => onToggle(category.id, e.target.checked)}
            disabled={category.isRequired}
            className="sr-only peer"
            aria-label={`${category.name} cookies ${isEnabled ? 'enabled' : 'disabled'}`}
          />
          <div
            className={`
              w-11 h-6 rounded-full transition-colors duration-200
              peer-focus:ring-2 peer-focus:ring-primary-light peer-focus:ring-offset-2
              ${
                category.isRequired
                  ? 'bg-primary-main cursor-not-allowed opacity-60'
                  : isEnabled
                    ? 'bg-primary-main'
                    : 'bg-gray-light'
              }
              after:content-[''] after:absolute after:top-[2px] after:left-[2px]
              after:bg-white after:rounded-full after:h-5 after:w-5
              after:transition-transform after:duration-200
              ${isEnabled ? 'after:translate-x-5' : 'after:translate-x-0'}
            `}
          />
        </label>
      </div>

      {/* Expandable description */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pb-4 px-1 pl-8">
              <Typography
                variant="body2"
                component="p"
                className="text-gray-main"
              >
                {category.description}
              </Typography>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialPreferences,
}) => {
  const [preferences, setPreferences] = React.useState<
    Omit<CookieConsentPreferences, 'consentDate' | 'consentVersion'>
  >(() => ({
    necessary: true,
    analytics: initialPreferences?.analytics ?? DEFAULT_PREFERENCES.analytics,
    marketing: initialPreferences?.marketing ?? DEFAULT_PREFERENCES.marketing,
    preferences:
      initialPreferences?.preferences ?? DEFAULT_PREFERENCES.preferences,
  }));

  // Sync preferences when modal opens with new initialPreferences
  // This is needed because useState initializer only runs once on mount
  React.useEffect(() => {
    if (isOpen && initialPreferences) {
      setPreferences({
        necessary: true,
        analytics: initialPreferences.analytics,
        marketing: initialPreferences.marketing,
        preferences: initialPreferences.preferences,
      });
    }
  }, [isOpen, initialPreferences]);

  const [expandedCategories, setExpandedCategories] = React.useState<
    Set<CookieCategory>
  >(new Set());

  const handleToggle = (categoryId: CookieCategory, enabled: boolean): void => {
    if (categoryId === CookieCategory.NECESSARY) return; // Can't disable necessary cookies

    setPreferences((prev) => ({
      ...prev,
      [categoryId]: enabled,
    }));
  };

  const handleExpandToggle = (categoryId: CookieCategory): void => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleSave = (): void => {
    onSave(preferences);
    onClose();
  };

  const handleAcceptAll = (): void => {
    onSave({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
    onClose();
  };

  const handleRejectAll = (): void => {
    onSave({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
    onClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      className="max-w-lg max-h-[90vh] shadow-lg shadow-primary-main/10"
      showCloseButton={false}
      ariaLabel="Privacy Preferences"
    >
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-light px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-pool flex items-center justify-center">
            <Icon
              name={IconNames.LOCK}
              size="lg"
              className="text-primary-main"
            />
          </div>
          <Typography
            id="cookie-preferences-title"
            variant="title5"
            component="h2"
            className="text-primary-main"
          >
            Privacy Preferences
          </Typography>
        </div>
        <ButtonIcon
          color="gray"
          size="sm"
          variant="text"
          onClick={onClose}
          icon={IconNames.X}
          ariaDescribedby="close-preferences-modal"
          aria-label="Close preferences modal"
        />
      </div>

      {/* Content */}
      <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-180px)]">
        <Typography
          variant="body2"
          component="p"
          className="text-gray-main mb-6"
        >
          We use cookies to enhance your browsing experience, serve personalized
          content, and analyze our traffic. You can customize your preferences
          below.
        </Typography>

        {/* Cookie Categories */}
        <div className="border border-gray-light rounded-xl overflow-hidden">
          {COOKIE_CATEGORIES.map((category) => (
            <CategoryToggle
              key={category.id}
              category={category}
              isEnabled={preferences[category.id]}
              onToggle={handleToggle}
              isExpanded={expandedCategories.has(category.id)}
              onExpandToggle={() => handleExpandToggle(category.id)}
            />
          ))}
        </div>

        {/* Privacy Policy Link */}
        <div className="mt-4 text-center">
          <Link to={routes.privacy} className="inline-flex items-center gap-1">
            <Typography
              variant="small"
              component="span"
              className="text-primary-main"
            >
              Read our full Privacy Policy
            </Typography>
            <Icon
              name={IconNames.ARROWUPRIGHTFROMSQUARE}
              size="xs"
              className="text-primary-main"
            />
          </Link>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="sticky bottom-0 bg-off-white border-t border-gray-light px-6 py-4">
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <Button
            variant="text"
            color="gray"
            size="sm"
            onClick={handleRejectAll}
            className="sm:flex-1 justify-center"
          >
            Reject All
          </Button>
          <Button
            variant="outlined"
            size="sm"
            onClick={handleSave}
            className="flex-1 justify-center"
          >
            Save Preferences
          </Button>
          <Button
            variant="filled"
            size="sm"
            onClick={handleAcceptAll}
            className="flex-1 justify-center"
          >
            Accept All
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

CookiePreferencesModal.displayName = 'CookiePreferencesModal';
